/**
 * Het rechtermuismenu in de Explorer, voor een object of een diagram.
 *
 * Toont alleen wat voor dít item en deze gebruiker kan: een lezer krijgt geen
 * hernoemen of verwijderen, het laatste diagram is niet te verwijderen, een
 * verwijderd object (in de prullenbak) niet nog eens. Zelfde opbouw als
 * `CanvasContextMenu`: sluit bij klik ernaast of Escape.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface ExplorerMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export interface ExplorerMenuStand {
  x: number;
  y: number;
  items: ExplorerMenuItem[];
}

export function ExplorerContextMenu({
  stand,
  onClose,
}: {
  stand: ExplorerMenuStand;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const opKlik = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("mousedown", opKlik);
    window.addEventListener("keydown", opToets, { capture: true });
    window.addEventListener("blur", onClose);
    return () => {
      window.removeEventListener("mousedown", opKlik);
      window.removeEventListener("keydown", opToets, { capture: true });
      window.removeEventListener("blur", onClose);
    };
  }, [onClose]);

  // Binnen het venster houden: een menu onderaan de Explorer zou anders
  // half onder de rand vallen.
  const left = Math.min(stand.x, window.innerWidth - 220);
  const top = Math.min(stand.y, window.innerHeight - (stand.items.length * 28 + 16));

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={t("explorer.contextMenu.label")}
      className="ledger fixed z-50 min-w-[200px] rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 text-sm shadow-xl ring-1 ring-(--ld-border)"
      style={{ left, top }}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {stand.items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          className={`block w-full px-3 py-1 text-left hover:bg-(--ld-raised) ${
            it.danger ? "text-(--ld-negative)" : "text-(--ld-text)"
          }`}
          onClick={() => {
            onClose();
            it.onSelect();
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
